/**
 * Water level at own position (spec 12) — Kartverket "Se havnivå" via the API
 * proxy. Same trigger/persistence pattern as weather: moved > 5 km or older
 * than 1 h. A 204 (no coverage here) clears the section rather than showing
 * an empty shell.
 */
import { create } from 'zustand';
import type { TideResponse } from '@boatnav/schemas';
import { haversineMeters } from '@boatnav/core';
import { getTideRecord, putTideRecord, clearTideRecord, type TideRecord } from '../../lib/db.js';

const REFETCH_DISTANCE_M = 5_000;
const REFETCH_AGE_MS = 60 * 60_000;

/** Age thresholds for the sheet's honesty rules — same shape as weather's
 *  (spec 11), scaled to tide's 1 h refetch cadence vs. weather's 30 min. */
export const TIDE_DIM_AGE_MS = 4 * 3_600_000;
export const TIDE_HIDE_AGE_MS = 24 * 3_600_000;

interface TideState {
  data: TideResponse | null;
  fetchedAt: number | null;
  atLat: number | null;
  atLon: number | null;
  loading: boolean;
  init: () => Promise<void>;
  maybeFetch: (lat: number, lon: number) => void;
  fetchFor: (lat: number, lon: number) => Promise<void>;
}

export const useTideStore = create<TideState>((set, get) => ({
  data: null,
  fetchedAt: null,
  atLat: null,
  atLon: null,
  loading: false,

  init: async () => {
    const rec = await getTideRecord().catch(() => undefined);
    if (rec) set({ data: rec.data, fetchedAt: rec.fetchedAt, atLat: rec.atLat, atLon: rec.atLon });
  },

  maybeFetch: (lat, lon) => {
    const { fetchedAt, atLat, atLon, loading } = get();
    if (loading) return;
    const now = Date.now();
    const stale = fetchedAt == null || now - fetchedAt > REFETCH_AGE_MS;
    const moved =
      atLat == null ||
      atLon == null ||
      haversineMeters({ lat, lon }, { lat: atLat, lon: atLon }) > REFETCH_DISTANCE_M;
    if (stale || moved) void get().fetchFor(lat, lon);
  },

  fetchFor: async (lat, lon) => {
    set({ loading: true });
    try {
      const res = await fetch(`/api/tide?lat=${lat}&lon=${lon}`);
      if (res.status === 204) {
        // No coverage at this position — hide the section rather than keep
        // showing a reading from somewhere else.
        set({ data: null, fetchedAt: null, atLat: lat, atLon: lon });
        await clearTideRecord().catch(() => undefined);
        return;
      }
      if (!res.ok) return; // keep whatever stale reading is already shown
      const data = (await res.json()) as TideResponse;
      const fetchedAt = Date.now();
      set({ data, fetchedAt, atLat: lat, atLon: lon });
      const rec: Omit<TideRecord, 'id'> = { data, fetchedAt, atLat: lat, atLon: lon };
      await putTideRecord(rec).catch(() => undefined);
    } catch {
      /* offline — keep the last persisted reading */
    } finally {
      set({ loading: false });
    }
  },
}));
